import { createContext, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, MEMBER_TOKEN_KEY } from "@/lib/api";

const MemberContext = createContext(null);
const usedAafCodes = new Set(); // exactly-once per code, even under StrictMode double effects
const PANEL_PAGES = ["/me", "/write"];
const AAF_RESUME = "roan_aaf_resume";

export function MemberProvider({ children }) {
  const [member, setMember] = useState(null);
  const [aaf, setAaf] = useState(null); // {busy} | {pending} | {verify} | {error}

  const signIn = (token, m) => {
    localStorage.setItem(MEMBER_TOKEN_KEY, token);
    setMember(m);
  };

  // Shared "after Firebase sign-in" steps used by Average AF hub codes, Google and email/password.
  const finishFirebaseSignIn = async (fb, user) => {
    const idToken = await user.getIdToken(true);
    const { data } = await api.post("/member/firebase-login", { id_token: idToken });
    if (data.needs_verification) {
      try { await fb.fbVerify(user); } catch (e) { /* already sent */ }
      return { verify: data.email };
    }
    if (data.needs_registration) return { pending: { ...data, idToken, name: data.name || user.displayName || "" } };
    signIn(data.token, data.member);
    fb.syncRoanProfile(user, data.member).catch((e) => console.warn("profile sync", e));
    toast.success(`Welcome back, ${data.member.name.split(" ")[0]}`);
    return { done: true };
  };

  const consumeAafCode = async (code) => {
    setAaf({ busy: true });
    const fb = await import("@/lib/firebase");
    if (!fb.firebaseEnabled) {
      setAaf(null);
      setMember(false);
      return;
    }
    try {
      if (localStorage.getItem(MEMBER_TOKEN_KEY) || fb.fbAuth().currentUser) {
        localStorage.removeItem(MEMBER_TOKEN_KEY);
        setMember(false);
        await fb.fbSignOut();
      }
      let user;
      try {
        user = await fb.exchangeLoginCode(code);
      } catch (e) {
        console.error("Average AF exchangeLoginCode failed", e?.code, e?.message, e?.details ?? e);
        toast.error(fb.AAF_EXPIRED_MSG);
        setMember(false);
        return setAaf({ error: fb.AAF_EXPIRED_MSG });
      }
      const result = await finishFirebaseSignIn(fb, user);
      if (!result.done) setMember(false);
      setAaf(result.done ? null : result);
      if (!result.done && !PANEL_PAGES.includes(window.location.pathname)) {
        sessionStorage.setItem(AAF_RESUME, "1");
        window.location.replace("/me");
      }
    } catch (e) {
      console.error("Average AF sign-in failed after code exchange", e);
      toast.error(errMsg(e));
      setMember(false);
      setAaf({ error: errMsg(e) });
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const aafCode = params.get("aaf_code");
    if (aafCode) {
      params.delete("aaf_code");
      window.history.replaceState({}, "", window.location.pathname + (params.toString() ? `?${params}` : "") + window.location.hash);
      if (usedAafCodes.has(aafCode)) return;
      usedAafCodes.add(aafCode);
      consumeAafCode(aafCode);
      return;
    }
    const mtoken = params.get("mtoken");
    if (mtoken) {
      params.delete("mtoken");
      const clean = window.location.pathname + (params.toString() ? `?${params}` : "") + window.location.hash;
      window.history.replaceState({}, "", clean);
      api
        .post("/member/verify", { token: mtoken })
        .then(({ data }) => {
          localStorage.setItem(MEMBER_TOKEN_KEY, data.token);
          setMember(data.member);
          toast.success(`Signed in as ${data.member.name}`);
        })
        .catch((e) => {
          toast.error(errMsg(e));
          setMember(false);
        });
      return;
    }
    if (!localStorage.getItem(MEMBER_TOKEN_KEY)) {
      setMember(false);
      return;
    }
    api
      .get("/member/me")
      .then((r) => setMember(r.data))
      .catch(() => {
        localStorage.removeItem(MEMBER_TOKEN_KEY);
        setMember(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Average AF sessions: ROAN token lasts 1 hour; renew silently with a fresh Firebase ID token (a disabled account can't get one).
  useEffect(() => {
    let unsub = () => {};
    let timer;
    import("@/lib/firebase").then((fb) => {
      if (!fb.firebaseEnabled) return;
      const renew = async (user) => {
        if (!user) return;
        try {
          const status = await fb.readAccountStatus(user.uid);
          if (status === "banned" || status === "suspended") {
            localStorage.removeItem(MEMBER_TOKEN_KEY);
            setMember(false);
            await fb.fbSignOut();
            toast.error("This Average AF account has been suspended.");
            return;
          }
          const idToken = await user.getIdToken();
          const { data } = await api.post("/member/firebase-login", { id_token: idToken });
          if (data.token) {
            localStorage.setItem(MEMBER_TOKEN_KEY, data.token);
            setMember(data.member);
          }
        } catch (e) {
          if (e?.response?.status === 403 || e?.code === "auth/user-disabled") {
            localStorage.removeItem(MEMBER_TOKEN_KEY);
            setMember(false);
            fb.fbSignOut().catch(() => {});
          }
        }
      };
      unsub = fb.onFbUser((user) => {
        clearInterval(timer);
        if (!user) return;
        if (localStorage.getItem(MEMBER_TOKEN_KEY)) renew(user);
        else if (sessionStorage.getItem(AAF_RESUME) && PANEL_PAGES.includes(window.location.pathname)) {
          sessionStorage.removeItem(AAF_RESUME);
          setAaf({ busy: true });
          finishFirebaseSignIn(fb, user).then((r) => { setAaf(r.done ? null : r); if (!r.done) setMember(false); }).catch((e) => { console.error("Average AF resume failed", e); setAaf({ error: errMsg(e) }); setMember(false); });
        }
        timer = setInterval(() => renew(fb.fbAuth().currentUser), 50 * 60 * 1000);
      });
    }).catch(() => {});
    return () => { unsub(); clearInterval(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verify = async (token) => {
    const { data } = await api.post("/member/verify", { token });
    localStorage.setItem(MEMBER_TOKEN_KEY, data.token);
    setMember(data.member);
    return data.member;
  };

  const signOut = () => {
    localStorage.removeItem(MEMBER_TOKEN_KEY);
    setMember(false);
    import("@/lib/firebase").then((m) => m.firebaseEnabled && m.fbSignOut()).catch(() => {});
  };

  const refresh = async () => {
    const r = await api.get("/member/me");
    setMember(r.data);
    return r.data;
  };

  return <MemberContext.Provider value={{ member, verify, signOut, signIn, refresh, aaf, clearAaf: () => setAaf(null), finishFirebaseSignIn }}>{children}</MemberContext.Provider>;
}

export const useMember = () => useContext(MemberContext);
